import React, { useCallback, useEffect, useState } from "react";
import { Form, Input, Button, Row, Col } from "antd";
import styled from "@emotion/styled";
import ContentHeader from "../../../components/shared/ContentHeader";
import { selectSelectedTenant } from "../../../store/user/selectors";
import { useDispatch, useSelector } from "react-redux";
import { getTenantDetailsById, saveTenant } from "../../../store/user/thunks";
import {
  BankOutlined,
  IdcardOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import Select from "../../../components/generic/Select";
import { employeesSizeOptions, industryOptions } from "../../../constant";
import { useRegions } from "../../../hooks/useRegions";
import { countryCodes } from "../../../constant/countryCodes";
import CompanyLogoUpload from "./CompanyLogoUpload";
import { uploadFile } from "../../../utils/uploadFile";
import { FILE_BASE_URL } from "../../../config";
import UpdateCasesForm from "./UpdateCasesForm";

const StyledFormContainer = styled.div`
  max-width: 1400px;
  margin: auto;
  .logo-upload {
    height: 120px !important;
  }
`;

const FormComponent = () => {
  const [form] = Form.useForm();

  const [submitting, setSubmitting] = useState(false);
  const { regionsOptions } = useRegions();
  const [fileList, setFileList] = useState([]);

  const dispatch = useDispatch();

  const selectedTenant = useSelector(selectSelectedTenant);

  useEffect(() => {
    if (!selectedTenant?.industry && selectedTenant?.id)
      dispatch(
        getTenantDetailsById({
          tenant_id: selectedTenant?.id,
        }),
      );
  }, [selectedTenant?.id]);

  useEffect(() => {
    form.setFieldsValue(selectedTenant);
    if (selectedTenant?.logo) {
      setFileList([
        {
          uid: "-1",
          name: "logo.png",
          status: "done",
          url: `${FILE_BASE_URL}${selectedTenant?.logo}`,
        },
      ]);
    }
  }, [selectedTenant]);

  const onFinish = useCallback(
    async (values) => {
      try {
        setSubmitting(true);
        const tenant_id = selectedTenant?.id;
        let logo = selectedTenant?.logo || "";
        const file = fileList?.[0]?.originFileObj;
        if (file) {
          const url = await uploadFile({
            file,
            fields: {
              tenant_id,
              is_logo: true,
            },
          });
          logo = url;
        }

        await dispatch(
          saveTenant({
            ...values,
            logo: logo || undefined,
            background_color: values?.background_color || undefined,
            tenant_id,
          }),
        ).unwrap();
      } catch (error) {
        console.log("🚀 ~ index.jsx:29 ~ FormComponent ~ error:", error);
      } finally {
        setSubmitting(false);
      }
    },
    [selectedTenant, fileList],
  );

  return (
    <StyledFormContainer>
      <ContentHeader title={"Business Details"}>
        {/* {selectedTenant?.role === "OWNER" && (
          <UpdateCasesForm tenant_id={selectedTenant?.id} />
        )} */}
      </ContentHeader>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        disabled={submitting}
      >
        <CompanyLogoUpload fileList={fileList} setFileList={setFileList} />
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="Company Name"
              name="name"
              rules={[{ required: true, message: "Please enter company name" }]}
            >
              <Input prefix={<BankOutlined />} placeholder="Tenant Name" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="Government Registered Country"
              name="gov_registered_country_code"
              rules={[
                { required: true, message: "Please select country code" },
              ]}
            >
              <Select
                placeholder="Select Industry"
                options={countryCodes.map((c) => ({
                  label: `${c.flag} ${c.name}`,
                  value: c.code,
                }))}
                defaultValue={"+44"}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="Government Registered Company ID"
              name="gov_registered_id"
              rules={[{ required: true, message: "Please enter company ID" }]}
            >
              <Input
                prefix={<IdcardOutlined />}
                placeholder="e.g. CRN (UK) or CIN (India)"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="Industry"
              name="industry"
              rules={[{ required: true, message: "Please select industry" }]}
            >
              <Select placeholder="Select Industry" options={industryOptions} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="Company Size"
              name="company_size"
              rules={[
                { required: true, message: "Please select company size" },
              ]}
            >
              <Select
                placeholder="Select Size"
                options={employeesSizeOptions}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="Primary Region"
              name="region_id"
              rules={[{ required: true, message: "Please select region" }]}
            >
              <Select
                placeholder="Select Region"
                suffixIcon={<GlobalOutlined />}
                options={regionsOptions}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Custom Domain (Optional)" name="domain">
              <Input placeholder="chat.yourcompany.com" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Tax ID" name="tax_id">
              <Input placeholder="e.g. VAT (UK) or GSTIN (India)" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Color" name="background_color">
              <Input type={"color"} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Submit
          </Button>
        </Form.Item>
      </Form>
    </StyledFormContainer>
  );
};

export default FormComponent;
